import { useMutation, useQueries, useQuery } from '@tanstack/react-query';
import type {
  BlendedAvailableProductResponse,
  BlendedProductPayload,
  BlendedProductResponse,
  BlendedProductsByProductIdResponse,
  ContinentApiResponse,
  ContinentReportResponse,
  GetQuickFinderResponse,
  HamburgerMenuResponse,
  IUrlProductDetailsResponse,
  JurisdictionAllowanceResponse,
  PanelDetailsResponse,
  PanelIdWiseDetailsResponse,
  RedirectResponse,
  RelatedToolsResponse,
  ShareJurisdictionLimitsResponse,
  TGetCategoryHomePageResponse,
  TGetCategoryNameResponse,
  TGetEncryptedQueryResponse,
  THamburgerMenuResponse,
} from '../types/products';
import { keyFactory } from '../util/keyFactory';
import { HttpClient } from './axiosConfig';

const getQuickFInders = async () => {
  const result = await HttpClient.get<GetQuickFinderResponse>(
    `/users/rendering-styles/quick-finder`
  );
  return result.data;
};

export const useGetCategoryHomePage = () => {
  return useQuery({
    queryKey: [keyFactory.getCategoryHomePage],
    queryFn: async () => {
      const result = await HttpClient.get<TGetCategoryHomePageResponse>(
        `/users/rendering-styles/category-home-page`
      );
      return result.data;
    },
  });
};

export const useGetProductCategory = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [keyFactory.getProductCategory],
    queryFn: async () => {
      const result = await HttpClient.get<THamburgerMenuResponse>(
        `/users/hamburger-menu/product-categories`
      );
      return result.data;
    },
    enabled: options?.enabled ?? true,
  });
};
export const useGetHamburgerMenuOptions = (
  categoryId: number,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: [keyFactory.getHamburgerMenuOptions, categoryId],
    queryFn: async () => {
      const result = await HttpClient.get<HamburgerMenuResponse>(
        `users/hamburger-menu-options?categoryId=${categoryId}`
      );
      return result.data;
    },
    enabled: (options?.enabled ?? true) && !!categoryId,
  });
};
export const useGetProductCategoryName = (categoryId: string) => {
  return useQuery({
    queryKey: [keyFactory.getProductCategory, categoryId],
    queryFn: async () => {
      const result = await HttpClient.get<TGetCategoryNameResponse>(
        `users/${categoryId}/product-categories`
      );
      return result.data;
    },
    enabled:  Number(categoryId) > 0,
  });
};

export const encryptQueryInput = async (input: string): Promise<string | undefined> => {
  const trimmed = input?.trim();
  if (!trimmed) return undefined;
  const result = await HttpClient.get<TGetEncryptedQueryResponse>(
    `commons/encrypt?input=${encodeURIComponent(trimmed)}`
  );
  return result.data?.data;
};

export const useGetEncryptQuery = (input: string) => {
  return useQuery({
    queryKey: [keyFactory.getEncryptQuery, input],
    queryFn: async () => {
     if (!input?.trim()) {
      return null;
     }
      const result = await HttpClient.get<TGetEncryptedQueryResponse>(
        `commons/encrypt?input=${encodeURIComponent(input)}`
      );
      return result.data;
    },
    enabled: !!input && input !== '',
  });
};

export const useGetProductDetails = (eventUrl: string) => {
  return useQuery({
    queryKey: [keyFactory.getProductCategory, eventUrl],
    queryFn: async () => {
      const result = await HttpClient.get<IUrlProductDetailsResponse>(
        `users/products/${eventUrl}/eurl`
      );
      return result.data;
    },
    enabled: !!eventUrl,
  });
};

export const useGetblendedAvailableProduct = (eventUrl: string) => {
  return useQuery({
    queryKey: [keyFactory.getBlendedAvailableProduct, eventUrl],
    queryFn: async () => {
      const result = await HttpClient.get<BlendedAvailableProductResponse>(
        `users/products/${eventUrl}/blended-products/availables`
      );
      return result.data;
    },
    enabled: !!eventUrl,
  });
};

export const getBlendedProductRedirectMutation = async (payload: BlendedProductPayload) => {
  const response = await HttpClient.get<BlendedProductResponse>(`users/products/blended-products/${payload.productId}/redirect?catId=${payload.catId}&categoryType=${payload.categoryType}`);
  return response.data.data;
};

export const getBlendedProductsByProductId = async (productId: string) => {
  const response = await HttpClient.get<BlendedProductsByProductIdResponse>(
    `/users/products/blended-products?productId=${productId}`
  );
  return response.data;
};


export const useFetchContinents = (eventUrl: string) => {
  return useQuery({
    queryKey: ['fetchContinents', eventUrl],
    queryFn: async () => {
      const result = await HttpClient.get<ContinentApiResponse>(
        `users/products/${eventUrl}/continent-details`
      );
      return result.data;
    },
    enabled: !!eventUrl,
  });
};

export const useGetPanelDetails = (eventUrl: string) => {
  return useQuery({
    queryKey: ['fetchPanelDetails', eventUrl],
    queryFn: async () => {
      const result = await HttpClient.get<PanelDetailsResponse>(
        `users/products/${eventUrl}/panel-details`
      );
      return result.data;
    },
    enabled: !!eventUrl,
  });
};

export const useGetSinglePanelDetails = (
  eventUrl: string,
  productPanelConfigId: number
) => {
  return useQuery({
    queryKey: ['fetchPanelDetails', eventUrl, productPanelConfigId],
    queryFn: async () => {
      const result = await HttpClient.get<PanelIdWiseDetailsResponse>(
        `users/products/${eventUrl}/panels/${productPanelConfigId}`
      );
      return result.data;
    },
    enabled: !!productPanelConfigId,
  });
};

// Hook to fetch multiple panel details in parallel
export const useGetMultiplePanelDetails = (
  eventUrl: string,
  panels: Array<{ productPanelConfigId: number }>
) => {
  return useQueries({
    queries: panels.map((panel) => ({
      queryKey: ['fetchPanelDetails', eventUrl, panel?.productPanelConfigId ?? 0],
      queryFn: async () => {
        const result = await HttpClient.get<PanelIdWiseDetailsResponse>(
          `users/products/${eventUrl}/panels/${panel?.productPanelConfigId ?? 0}`
        );
        return result.data;
      },
      enabled: !!panel?.productPanelConfigId && !!eventUrl,
    })),
  });
};
export const useGetReportRelatedLinks = (eventUrl: string) => {
  return useQuery({
    queryKey: [keyFactory.getReportRelatedTools, eventUrl],
    queryFn: async () => {
      const result = await HttpClient.get<RelatedToolsResponse>(
        `users/products/${eventUrl}/related-tools`
      );
      return result.data;
    },
    enabled: !!eventUrl,
  });
};

export const useContinentReports = (
  eventUrl: string,
  continentId?: number,
  opinionIds?: string
) =>
  useQuery({
    queryKey: ['continentReports', eventUrl, continentId, opinionIds],
    queryFn: async () => {
      const result = await HttpClient.get<ContinentReportResponse>(
        `users/products/${eventUrl}/continent/reports?continentId=${continentId}&opinionIds=${opinionIds}`
      );
      return result.data;
    },
    enabled: !!continentId,
  });
export const useGetShareJurisdictionLimits = (productId: number) => {
  return useQuery({
    queryKey: ['getShareJurisdictionLimits', productId],
    queryFn: async () => {
      const result = await HttpClient.get<ShareJurisdictionLimitsResponse>(
        `users/products/share-jurisdiction-limits?productId=${productId}`
      );
      return result.data;
    },
    enabled: !!productId && productId > 0,
  });
};

export const useGetJurisdictionsAllowance = (eventUrl: string) => {
  return useQuery({
    queryKey: [keyFactory.getJurisdictionsAllowance, eventUrl],
    queryFn: async () => {
      const result = await HttpClient.get<JurisdictionAllowanceResponse>(
        `users/products/${eventUrl}/jurisdiction-allowances`
      );
      return result.data;
    },
    enabled: !!eventUrl,
  });
};
export const getRedirectKey = async (productId: string, redirectTo?: string) => {
  const query = new URLSearchParams();
  if (redirectTo) {
    query.append('redirectTo', redirectTo);
  }
  const queryString = query.toString();
  const url = queryString
    ? `/users/products/${productId}/redirect?${queryString}`
    : `/users/products/${productId}/redirect`;
  const response = await HttpClient.get<RedirectResponse>(url);
  return response.data;
};
export const useGetRedirectKey = () => {
  return useMutation({
    mutationFn: (productId: string) => getRedirectKey(productId),
  });
};
export const productService = {
  getQuickFInders,
};
